import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import {
  getDocGenerationState,
  logDocGenerationInfo,
  logDocGenerationWarn,
} from "src/utils/documentGenerationDebug";
import { buildDocumentsPageBreadcrumbItems } from "src/utils/documentsBreadcrumbs";
import DocViewer from "../components/DocViewer";
import {
  useDocProgressMap,
  useGetDocumentWithAllVersions,
  useGetFolders,
} from "../hooks";
import { Loading } from "./Loading";
import { useQmsLegacyRoute } from "./useQmsLegacyRoute";

const QMS = observer(() => {
  const { deviceId = "", docId: routeDocId = "", orgId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const versionId = searchParams.get("v") || undefined;
  const { documents, isLoadingDocuments, isRerouting, resolvedDocId } =
    useQmsLegacyRoute();
  const { data: docWithAllVersions } = useGetDocumentWithAllVersions(
    orgId,
    resolvedDocId ?? ""
  );
  const docProgressMap = useDocProgressMap(deviceId);
  const isLoadingQmsDocuments = isLoadingDocuments;
  const docId = resolvedDocId ?? undefined;

  const selectedDocumentFromList = docId
    ? documents?.find((doc) => doc.id === docId)
    : undefined;
  const selectedDocument = docWithAllVersions ?? selectedDocumentFromList;
  const isLifecycleDeleted = Boolean(
    docId &&
      documents &&
      docWithAllVersions &&
      !documents.some((document) => document.id === docId)
  );
  const foldersQuery = useGetFolders({
    enabled: Boolean(orgId),
    orgId,
  });
  const folders = foldersQuery.data ?? [];
  const breadcrumbItems = buildDocumentsPageBreadcrumbItems({
    activeDocument: selectedDocument,
    folders,
    orgId,
  });
  const isBreadcrumbItemsLoading =
    foldersQuery.isLoading && foldersQuery.data === undefined;
  const matchedVersion = docWithAllVersions?.versions.find(
    (version) => version.id === versionId
  );
  const fallbackVersion = docWithAllVersions?.versions[0];
  const selectedDocVersion = matchedVersion ?? fallbackVersion;
  const selectedVersionHasSnapshot = selectedDocVersion
    ? selectedDocVersion.snapshot != null
    : null;
  const selectedVersionHasContentTemplate =
    selectedDocVersion ? selectedDocVersion.contentTemplate != null : null;

  useEffect(() => {
    if (isRerouting) {
      return;
    }

    logDocGenerationInfo("qms.version-selection", {
      ...getDocGenerationState({
        docId:
          docWithAllVersions?.id ??
          selectedDocument?.id ??
          routeDocId ??
          undefined,
        version: selectedDocVersion,
      }),
      routeDocId: routeDocId || null,
      routeVersionId: versionId ?? null,
      matchedRouteVersion: Boolean(matchedVersion),
      usedFallbackVersion: Boolean(!matchedVersion && fallbackVersion),
      versionCount: docWithAllVersions?.versions.length ?? 0,
      hasDocumentWithAllVersions: Boolean(docWithAllVersions),
      hasSnapshot: selectedVersionHasSnapshot,
      hasContentTemplate: selectedVersionHasContentTemplate,
    });
  }, [
    docWithAllVersions,
    fallbackVersion,
    isRerouting,
    matchedVersion,
    routeDocId,
    selectedDocVersion,
    selectedDocument,
    selectedVersionHasContentTemplate,
    selectedVersionHasSnapshot,
    versionId,
  ]);

  useEffect(() => {
    if (isRerouting || !versionId || matchedVersion || !docWithAllVersions) {
      return;
    }

    logDocGenerationWarn("qms.route-version-missing", {
      ...getDocGenerationState({
        docId: docWithAllVersions.id,
        version: selectedDocVersion,
      }),
      routeDocId: routeDocId || null,
      routeVersionId: versionId,
      matchedRouteVersion: false,
      usedFallbackVersion: Boolean(fallbackVersion),
      versionCount: docWithAllVersions.versions.length,
      hasSnapshot: selectedVersionHasSnapshot,
      hasContentTemplate: selectedVersionHasContentTemplate,
    });
  }, [
    docWithAllVersions,
    fallbackVersion,
    isRerouting,
    matchedVersion,
    routeDocId,
    selectedDocVersion,
    selectedVersionHasContentTemplate,
    selectedVersionHasSnapshot,
    versionId,
  ]);

  useEffect(() => {
    if (!docWithAllVersions || isRerouting) {
      return;
    }

    if (docWithAllVersions.versions.length === 0) {
      if (versionId) {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete("v");
        setSearchParams(nextSearchParams, { replace: true });
      }
      return;
    }

    if (!matchedVersion && fallbackVersion && versionId !== fallbackVersion.id) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("v", fallbackVersion.id);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    docWithAllVersions,
    fallbackVersion,
    isRerouting,
    matchedVersion,
    searchParams,
    setSearchParams,
    versionId,
  ]);

  if (isRerouting) {
    return <Loading />;
  }

  return (
    <div className="flex flex-1 flex-col h-full">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            isLoading={isBreadcrumbItemsLoading}
            items={breadcrumbItems}
          />
        }
      />

      <div className="flex flex-1 min-h-0">
        <div className="flex h-full min-w-0 flex-1 justify-center">
          <DocViewer
            documents={documents}
            isLoading={isLoadingQmsDocuments}
            deviceId={deviceId}
            docId={docId}
            documentWithAllVersions={docWithAllVersions}
            isLifecycleDeleted={isLifecycleDeleted}
            selectedDocVersion={selectedDocVersion}
            docProgress={docId ? docProgressMap[docId] : undefined}
          />
        </div>
      </div>
    </div>
  );
});

export default QMS;
